import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, Edit } from "lucide-react";
import { notifications } from "@mantine/notifications";
import { Endpoints } from "../../../../lib/endpoints";
import { PerformRequest } from "../../../../lib/usePerformRequest";
import { InlineLoader } from "../../../../components/Loader/LoadingOverlay";
import { ButtonLoader } from "../../../../components/Loader";

interface Transaction {
  id: string;
  property_type?: string;
  units?: string;
  client_name?: string;
  user?: string;
  amount?: number;
  status?: "PAID" | "PENDING" | "PROCESSING" | "pending" | "paid" | "processing";
  date?: string;
  time?: string;
  reference?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: any;
}

export default function TransactionDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (id) {
      fetchTransactionDetail();
    }
  }, [id]);

  async function fetchTransactionDetail() {
    if (!id) return;

    setIsLoading(true);
    try {
      const endpoint = Endpoints.GetTransactionDetail(id);
      const response = await PerformRequest<Transaction>({
        method: endpoint.method,
        route: endpoint.route,
      });

      if (response.status === 200 && response.data) {
        setTransaction(response.data);
      } else {
        const errorMessage = response?.message || "Failed to load transaction details. Please try again.";
        notifications.show({
          title: "Error",
          message: errorMessage,
          color: "red",
        });
        navigate("/d/transactions");
      }
    } catch (error: any) {
      const errorMessage = error?.message || "Failed to load transaction details. Please try again.";
      notifications.show({
        title: "Error",
        message: errorMessage,
        color: "red",
      });
      navigate("/d/transactions");
    } finally {
      setIsLoading(false);
    }
  }

  const handleDownloadCertificate = async () => {
    if (!id) return;

    setIsDownloading(true);
    try {
      const endpoint = Endpoints.GetTransactionCertificate(id);
      const response = await PerformRequest<Blob>({
        method: endpoint.method,
        route: endpoint.route,
      });

      if (response.status === 200 && response.data) {
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `transaction-certificate-${id}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        notifications.show({
          title: "Success",
          message: "Certificate downloaded successfully!",
          color: "green",
        });
      } else {
        const errorMessage = response?.message || "Failed to download certificate. Please try again.";
        notifications.show({
          title: "Error",
          message: errorMessage,
          color: "red",
        });
      }
    } catch (error: any) {
      const errorMessage = error?.message || "Failed to download certificate. Please try again.";
      notifications.show({
        title: "Error",
        message: errorMessage,
        color: "red",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-page__header">
          <button
            onClick={() => navigate("/d/transactions")}
            className="dashboard-page__back-button"
            type="button"
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
            <span>Transaction Details</span>
          </button>
        </div>
        <InlineLoader isLoading={true} text="Loading transaction details..." />
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-page__header">
          <button
            onClick={() => navigate("/d/transactions")}
            className="dashboard-page__back-button"
            type="button"
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
            <span>Transaction Details</span>
          </button>
        </div>
        <div className="dashboard-empty">Transaction not found</div>
      </div>
    );
  }

  const statusValue = transaction?.status?.toLowerCase() || "pending";
  const statusTone =
    statusValue === "paid"
      ? "success"
      : statusValue === "processing"
      ? "processing"
      : "pending";

  const displayName = transaction?.client_name || transaction?.user || "N/A";
  const displayAmount = transaction?.amount
    ? `₦${Number(transaction.amount).toLocaleString("en-NG", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : "N/A";

  const displayDate = transaction?.date || transaction?.created_at
    ? new Date(transaction.date || transaction.created_at || "").toLocaleDateString("en-NG", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "N/A";

  const displayTime = transaction?.time || transaction?.created_at
    ? new Date(transaction.time || transaction.created_at || "").toLocaleTimeString("en-NG", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  return (
    <div className="dashboard-page">
      <div className="dashboard-page__header">
        <button
          onClick={() => navigate("/d/transactions")}
          className="dashboard-page__back-button"
          type="button"
          aria-label="Go back"
        >
          <ArrowLeft size={20} />
          <span>Transaction Details</span>
        </button>
        <button
          onClick={() => navigate(`/d/transactions?edit=${transaction.id}`)}
          className="dashboard-button flex items-center justify-center gap-2"
          type="button"
          disabled={isLoading}
        >
          <Edit size={16} />
          <span>Edit Transaction</span>
        </button>
      </div>

      <section className="dashboard-panel shadow-[var(--card-shadow)] rounded-2xl">
        <div className="dashboard-panel__header">
          <h2 className="dashboard-panel__title">Transaction Information</h2>
        </div>

        <div className="dashboard-detail-grid">
          <div className="dashboard-detail-item">
            <div className="dashboard-detail-item__label">Transaction ID:</div>
            <div className="dashboard-detail-item__value font-mono">
              {transaction?.id || transaction?.reference || "N/A"}
            </div>
          </div>

          <div className="dashboard-detail-item">
            <div className="dashboard-detail-item__label">User/Client</div>
            <div className="dashboard-detail-item__value">{displayName}</div>
          </div>

          <div className="dashboard-detail-item">
            <div className="dashboard-detail-item__label">Amount</div>
            <div className="dashboard-detail-item__value font-semibold text-lg">
              {displayAmount}
            </div>
          </div>

          <div className="dashboard-detail-item">
            <div className="dashboard-detail-item__label">Status</div>
            <div className="dashboard-detail-item__value">
              <span
                className={
                  statusTone === "success"
                    ? "badge badge--success"
                    : statusTone === "processing"
                    ? "badge badge--processing"
                    : "badge badge--pending"
                }
              >
                {transaction?.status?.toUpperCase() || "PENDING"}
              </span>
            </div>
          </div>

          {transaction?.property_type && (
            <div className="dashboard-detail-item">
              <div className="dashboard-detail-item__label">Property Type</div>
              <div className="dashboard-detail-item__value">
                {transaction.property_type}
              </div>
            </div>
          )}

          {transaction?.units && (
            <div className="dashboard-detail-item">
              <div className="dashboard-detail-item__label">Units</div>
              <div className="dashboard-detail-item__value">{transaction.units}</div>
            </div>
          )}

          <div className="dashboard-detail-item">
            <div className="dashboard-detail-item__label">Date Created</div>
            <div className="dashboard-detail-item__value">
              {displayDate}
              {displayTime && (
                <div className="text-sm text-gray-500 mt-1">{displayTime}</div>
              )}
            </div>
          </div>

          {transaction?.updated_at && (
            <div className="dashboard-detail-item">
              <div className="dashboard-detail-item__label">Last Updated</div>
              <div className="dashboard-detail-item__value">
                {new Date(transaction.updated_at).toLocaleDateString("en-NG", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
            </div>
          )}
        </div>

        <div className="dashboard-panel__footer flex items-center justify-end gap-3 mt-6">
          <button
            onClick={handleDownloadCertificate}
            className="dashboard-button dashboard-button--secondary flex items-center justify-center gap-2"
            type="button"
            disabled={isDownloading || isLoading}
          >
            {isDownloading ? (
              <>
                <ButtonLoader color="green" />
                <span>Downloading...</span>
              </>
            ) : (
              <>
                <Download size={16} />
                <span>Download Certificate</span>
              </>
            )}
          </button>
        </div>
      </section>
    </div>
  );
}


